'use client';

import { useState } from 'react';
import NextLink from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { Button, Input } from '@/components/ui';
import { useAuthStore } from '@/stores/authStore';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

const LoginPage = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { login, isLoading, error } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);

  const from = searchParams.get('from') || '/dashboard';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      await login(data.email, data.password);
      // Set a cookie so middleware allows access to protected routes.
      // The middleware checks for this cookie since Supabase stores its session
      // in localStorage (not cookies) and middleware can't read localStorage.
      document.cookie = 'axis-demo-session=true; path=/; max-age=86400; SameSite=Lax';
      router.replace(from);
    } catch {
      // Error is already set in the auth store, no need to handle here
    }
  };

  const handleDemoLogin = async () => {
    try {
      await login('demo@axis.app', 'demo123');
      // Set a cookie so middleware allows access to protected routes
      document.cookie = 'axis-demo-session=true; path=/; max-age=86400; SameSite=Lax';
      router.replace('/dashboard');
    } catch {
      // Error is already set in the auth store
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2 text-center">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
          Welcome back
        </h1>
        <p className="text-gray-500 dark:text-secondary-300">
          Sign in to your account to continue
        </p>
      </div>

      {error && (
        <div className="rounded-lg bg-danger-50 dark:bg-danger-500/10 border border-danger-200 dark:border-danger-500/30 px-4 py-3 text-sm text-danger-700 dark:text-danger-400">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label="Email address"
          type="email"
          placeholder="you@example.com"
          leftIcon={<Mail className="h-4 w-4" />}
          error={errors.email?.message}
          {...register('email')}
        />

        <Input
          label="Password"
          type={showPassword ? 'text' : 'password'}
          placeholder="Enter your password"
          leftIcon={<Lock className="h-4 w-4" />}
          rightIcon={
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-gray-400 hover:text-gray-600 dark:text-secondary-500 dark:hover:text-secondary-300"
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          }
          error={errors.password?.message}
          {...register('password')}
        />

        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 dark:border-secondary-600 text-primary-600 focus:ring-primary-500 dark:bg-secondary-800"
            />
            <span className="text-sm text-gray-600 dark:text-secondary-400">Remember me</span>
          </label>
          <NextLink
            href="/forgot-password"
            className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
          >
            Forgot password?
          </NextLink>
        </div>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          isLoading={isLoading}
        >
          Sign in
        </Button>
      </form>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200 dark:border-white/[0.08]" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="bg-white dark:bg-secondary-950 px-2 text-gray-400 dark:text-secondary-500">Or continue with</span>
        </div>
      </div>

      <Button
        type="button"
        variant="outline"
        size="lg"
        fullWidth
        onClick={handleDemoLogin}
      >
        Try Demo Account
      </Button>

      <p className="text-center text-sm text-gray-500 dark:text-secondary-400">
        Don&apos;t have an account?{' '}
        <NextLink
          href="/register"
          className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
        >
          Sign up
        </NextLink>
      </p>
    </div>
  );
};

export { LoginPage };
